import { useMemo } from 'react';
import {
  IJobPostDetailEntity,
  JobPostingStatus,
  useGetCandidateList,
  useGetJobPostDetail,
  useGetOnsiteInterviewStatusCount,
} from '~/domain/PermanentJobs';

interface IUseJobOverviewDataReturn {
  jobPosting: IJobPostDetailEntity | undefined;
  hasCandidatesToHire: boolean;
  hasCandidateScheduledOnsiteInterviews: boolean;
  hasScheduledOnsiteInterviews: boolean;
  isLoading: boolean;
}

export const useJobOverviewData = (postingId?: string): IUseJobOverviewDataReturn => {
  // Fetch job posting detail
  const { data: jobPosting, isLoading: isLoadingJobPosting } = useGetJobPostDetail(postingId);

  // Fetch candidate statistics (limit: 1 for performance since we only need counts)
  const { statistics, isInitialLoading: isLoadingStatistics } = useGetCandidateList(
    {
      jobPostingId: postingId || '',
      limit: 1,
    },
    { enabled: !!postingId, refetchOnWindowFocus: false },
  );
  const { isLoading: isOIStatusCountsLoading, statistic: oiStatusCounts } =
    useGetOnsiteInterviewStatusCount(
      {
        jobPostingId: postingId || '',
      },
      {
        enabled: jobPosting?.status === JobPostingStatus.Closed,
        key: 'GET_OI_STATUS_COUNT',
      },
    );

  const hasCandidatesToHire = useMemo(() => {
    return (statistics?.tabs?.interviewed ?? 0) > 0 || (statistics?.tabs?.hired ?? 0) > 0;
  }, [statistics]);

  const hasCandidateScheduledOnsiteInterviews = useMemo(() => {
    return (statistics?.tabs?.scheduled ?? 0) > 0;
  }, [statistics]);

  const isLoading = isLoadingJobPosting || isLoadingStatistics || isOIStatusCountsLoading;

  return {
    jobPosting,
    hasCandidatesToHire,
    hasCandidateScheduledOnsiteInterviews,
    hasScheduledOnsiteInterviews: oiStatusCounts.scheduled > 0,
    isLoading,
  };
};
